import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const VirtualTourSection = () => {
  const [activeTab, setActiveTab] = useState('laboratories');

  const tourSections = {
    laboratories: {
      title: "State-of-the-Art Facilities",
      description: "Explore our modern labs equipped with industry-standard equipment and technology",
      facilities: [
        {
          name: "Computer Labs",
          image: "/assets/images/Trainees_ICTLabSession3-min.JPG",
          description: "Well equiped computer labs with latest software development tools and technologies",
          features: ["Latest Generation Computers", "Repair and Maintenance Facilities", "Well equiped Networking and Cyber Security Labs"]
        },
        {
          name: "Electrical and Electronics Workshop",
          image: "/assets/images/Electrical_TraineesPractical5-min.JPG",
          description: "Comprehensive electronics lab with modern testing equipment",
          features: ["Digital Oscilloscopes", "Function Generators", "PCB Design Station"]
        },
        {
          name: "Building and Civil Engineering Workshop",
          image: "/assets/images/Trainees_Building3-min.jpg",
          description: "Industrial-grade machinery for hands-on Civil Engineering training",
          features: ["CNC Machines", "3D Printers", "Precision Tools"]
        },
        {
          name: "Agriculture and Environmental Study",
          image: "/assets/images/FishPond_Farm-min.JPG",
          description: "Specialized agricultural workshops with modern farming equipment and machinery for practical training",
          features: ["", "Irrigation Systems", "Soil Testing Labs"]
        },
         {
          name: "Modern Facilities for Beauty and Fashion Design",
          image: "/assets/images/Equipment2_Cosmetology-min.JPG",
          description: "Contemporary beauty and fashion design studios with professional equipment and creative spaces",
          features: ["Salon Equipment", "Sewing Machines", "Design Software"]
        },
         {
          name: "Modern Facilities for Hospitality and Catering and Accommodation",
          image: "/assets/images/Food_Production-min.jpg",
          description: "Professional hospitality training facilities including commercial kitchens, restaurants, and accommodation mock-ups",
          features: ["Commercial Kitchens", "Restaurant Training", "Hotel Management Labs"]
        }
      ]
    },
    campus: {
      title: "Campus Infrastructure",
      description: "Tour our modern campus designed for optimal learning and student life",
      facilities: [
        {
          name: "Main Academic Block",
          image: "/assets/images/GateEntrance-min.jpg",
          description: "Modern classrooms with smart boards and audio-visual systems",
          features: ["Smart Classrooms", "Seminar Halls", "Faculty Offices"]
        },
        {
          name: "Library & Resource Center",
          image: "/assets/images/StudentCenter_2-min.JPG",
          description: "Extensive collection of technical books and digital resources",
          features: ["1000+ Books", "Digital Library", "Study Areas"]
        },
        {
          name: "Student Center & Recreational Areas",
          image: "assets/images/students.jpg",
          description: "Spaces for relaxation and extracurricular activities",
          features: ["Sports Facilities", "Cafeteria", "Student Lounge"]
        }
      ]
    }
  };

  const tabs = [
    { key: 'laboratories', label: 'Modern Facilities', icon: 'Microscope' },
    { key: 'campus', label: 'Campus Life', icon: 'Building2' },
  ];

  return (
    <section className="py-20 bg-blue-100">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-4">Take a Look at our Institute </h2>
          <p className="text-xl text-black max-w-3xl mx-auto">
            Take a comprehensive overview tour of our modern facilities and infrastructure that foster an optimal learning environment.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-12">
          <div className="bg-muted rounded-xl p-2 inline-flex">
            {tabs?.map((tab) => (
              <button
                key={tab?.key}
                onClick={() => setActiveTab(tab?.key)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  activeTab === tab?.key
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={20} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Active Tab Content */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-foreground mb-4">
              {tourSections?.[activeTab]?.title}
            </h3>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {tourSections?.[activeTab]?.description}
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {tourSections?.[activeTab]?.facilities?.map((facility, index) => (
              <div key={index} className="bg-card rounded-2xl overflow-hidden shadow-card-lg border border-border hover:shadow-xl transition-shadow duration-300">
                <div className="relative overflow-hidden h-48">
                  <Image
                    src={facility?.image}
                    alt={facility?.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4 bg-primary/90 backdrop-blur-sm text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                    360° View
                  </div>
                </div>
                
                <div className="p-6">
                  <h4 className="text-xl font-bold text-foreground mb-3">{facility?.name}</h4>
                  <p className="text-muted-foreground mb-4 leading-relaxed">
                    {facility?.description}
                  </p>
                  
                  <div className="space-y-2 mb-6">
                    {facility?.features?.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-2">
                        <Icon name="CheckCircle" size={16} className="text-success" />
                        <span className="text-sm text-muted-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Interactive Map */}
        <div className="bg-card rounded-2xl p-8 shadow-card-lg border border-border">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-foreground mb-2">Institute Location</h3>
            <p className="text-muted-foreground">Visit us at our modern facility</p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <div className="bg-muted rounded-xl overflow-hidden h-80">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title="KTTI Location"
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1994.8950167547384!2d37.74920108862461!3d0.21575133672097765!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x178838ef2c157f43%3A0x2703831c509ff321!2sKarumo%20Technical%20Training%20Institute!5e0!3m2!1sen!2ske!4v1761139421241!5m2!1sen!2ske"
                  className="border-0">
                  </iframe>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-3">
                <Icon name="MapPin" size={20} className="text-primary mt-1" />
                <div>
                  <h4 className="font-semibold text-foreground mb-1">Address</h4>
                 <ul className="space-y-2 text-sm text-gray-600">
                <li>📍 KARUMO TECHNICAL TRAINING INSTITUTE</li>
                <li>P.O BOX 276-60602 KIANJAI, MERU</li>
                <li>📞 +254(0)705636443</li>
                <li>✉️ info@karumotti.ac.ke</li>
              </ul>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Icon name="Clock" size={20} className="text-primary mt-1" />
                <div>
                  <h4 className="font-semibold text-foreground mb-1">Visiting Hours</h4>
                  <p className="text-sm text-muted-foreground">
                    Mon - Fri: 8:00 AM - 5:00 PM
                  </p>
                </div>
              </div>
              
              <Button variant="default" fullWidth iconName="Navigation" iconPosition="left">
                Get Directions
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VirtualTourSection;